<template>
    <div>
        <b-input-group class="input-group-merge w-100 mr-1 round group-name">
            <b-input-group-prepend :class="formError ? 'is-invalid' : ''" is-text>
                <font-awesome-icon class="text-muted" icon="user-group" />
            </b-input-group-prepend>
            <b-form-input
                v-model="groupName"
                :placeholder="$t('team-chats.give-group-name')"
                :state="formError ? false : null"
                @change="groupNameChange"
            />
        </b-input-group>

        <p class="group-counter">
            {{ $t("team-chats.in-group-chat") }}:
            <span>{{ selectedUsers && selectedUsers.length ? selectedUsers.length : 0 }}</span>
        </p>

        <div class="group-actions">
            <b-button v-b-modal="'teamchat-users'" variant="primary">{{ $t("team-chats.add-people") }}</b-button>
            <b-button v-if="selectedUsers.length" variant="light" @click="cleanUsers">
                {{ $t("team-chats.remove-all") }}
            </b-button>
        </div>

        <perfect-scrollbar class="team-chat-user-selection-wrapper list-group scroll-area">
            <b-overlay>
                <b-nav vertical>
                    <team-user-item
                        v-for="user of selectedList"
                        :key="user.id"
                        :user="user"
                        @remove-team-chat-user="removeTeamChatUser"
                    />
                </b-nav>
            </b-overlay>
        </perfect-scrollbar>

        <div>
            <b-button v-if="selectedUsers.length && !editChat" variant="primary" @click="createGroupChat">
                {{ $t("team-chats.create-group") }}
            </b-button>
            <b-button v-if="selectedUsers.length && editChat" variant="primary" @click="updateGroupChat">
                {{ $t("team-chats.update-group") }}
            </b-button>
        </div>
    </div>
</template>

<script>
import {
    BButton,
    BFormInput,
    BFormRadioGroup,
    BInputGroup,
    BInputGroupPrepend,
    BLink,
    BNav,
    BOverlay,
} from "bootstrap-vue";
import { ref } from "@vue/composition-api";
import TeamUserItem from "./teamUserItem.vue";
import axios from "@/libs/axios";
import useActiveTeamChat from "../useActiveTeamChat";
import useTeamChat from "../useTeamChat";
import { useToast } from "vue-toastification/composition";

export default {
    components: {
        BInputGroup,
        BFormRadioGroup,
        BInputGroupPrepend,
        BFormInput,
        BButton,
        BLink,
        BOverlay,
        BNav,
        TeamUserItem,
    },
    props: {
        users: Array,
        selectedUsers: Array,
        editChat: Number,
        toggleCreateTeamChat: Function,
    },
    computed: {
        selectedList() {
            return this.users.filter((user) => this.selectedUsers.includes(user.id));
        },
    },
    methods: {
        createGroupChat() {
            if (this.groupName === "") {
                this.formError = true;
                return;
            }
            axios
                .post(`/team-chats`, {
                    name: this.groupName,
                    type: "group",
                    participants: this.selectedUsers.map((userId) => ({ userId })),
                })
                .then((response) => {
                    this.$root.$router.push(`/team-chat/${response.data.id}`);
                    this.$emit("toggle-create-team-chat");
                    this.$emit("remove-selected-users");

                    this.toast(this.$t("team-chats.create.success"), {
                        title: this.$t("team-chats.create.success-title"),
                        variant: "success",
                    });
                })
                .catch(() => {
                    this.toast(this.$t("team-chats.create.failure"), {
                        title: this.$t("team-chats.create.failure-title"),
                        variant: "danger",
                    });
                });
        },
        updateGroupChat() {
            if (this.groupName === "") {
                this.formError = true;
                return;
            }
            const usersToAdd = [];
            const usersToRemove = [];

            const activeParticipants = this.activeChat.participants.reduce((ids, participant) => {
                if (!participant.leftAt) {
                    ids.push(participant.userId);
                }
                return ids;
            }, []);

            this.selectedUsers.forEach((userId) => {
                if (!activeParticipants.includes(userId)) {
                    usersToAdd.push(userId);
                }
            });

            this.activeChat.participants.forEach((participant) => {
                if (
                    activeParticipants.includes(participant.userId) &&
                    !this.selectedUsers.includes(participant.userId)
                ) {
                    usersToRemove.push(participant.id);
                }
            });

            const updateChat = axios.patch(`/team-chats/${this.$props.editChat}`, {
                name: this.groupName,
            });

            Promise.all([
                updateChat,
                ...usersToAdd.map((userId) =>
                    axios.post(`/team-chats/${this.$props.editChat}/participants`, { userId })
                ),
                ...usersToRemove.map((participantId) =>
                    axios.delete(`/team-chats/${this.$props.editChat}/participants/${participantId}`)
                ),
            ])
                .then(() => {
                    this.$emit("set-edited-chat", null);

                    this.toast(this.$t("team-chats.update.success"), {
                        title: this.$t("team-chats.update.success-title"),
                        variant: "success",
                    });

                    const { chats } = useTeamChat();
                    const { activeChat } = useActiveTeamChat();

                    activeChat.value = {
                        ...activeChat.value,
                        name: this.groupName,
                    };

                    const chat = chats.value.find((chat) => chat.id === this.$props.editChat);
                    if (chat) {
                        chat.name = this.groupName;
                    }
                })
                .catch(() => {
                    this.toast(this.$t("team-chats.update.failure"), {
                        title: this.$t("team-chats.update.failure-title"),
                        variant: "danger",
                    });
                });
        },
        groupNameChange(value) {
            if (this.formError && value !== "") {
                this.formError = false;
            }
        },
        removeTeamChatUser(id) {
            this.$emit("remove-selected-user", id);
        },
    },
    emits: [
        "add-selected-users",
        "set-edited-chat",
        "toggle-create-team-chat",
        "remove-selected-user",
        "remove-selected-users",
    ],
    setup(props, context) {
        const { activeChat } = useActiveTeamChat();
        const groupName = ref(props.editChat ? activeChat.value?.name : "");
        const toast = useToast();
        const formError = ref(false);

        const cleanUsers = () => {
            context.emit("remove-selected-users");
        };

        return {
            groupName,
            activeChat,
            cleanUsers,
            toast,
            formError,
        };
    },
};
</script>

<style lang="scss">
@import "~@/assets/scss/modules/team-chat/teamChatCreateGroupChat.scss";
</style>
